import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Brain,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Database,
  Code,
  BookOpen,
  FileText,
  Zap,
  ArrowLeft,
  Settings,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getBranches } from '@/api/deployments';

interface KnowledgeStatus {
  indexed: boolean;
  indexedAt?: string;
  stats?: {
    modelsIndexed?: number;
    fieldsIndexed?: number;
    patternsIndexed?: number;
    terminologyIndexed?: number;
    codeChunksIndexed?: number;
  };
}

interface IndexResult {
  success: boolean;
  message?: string;
  error?: string;
  modelsIndexed?: number;
  fieldsIndexed?: number;
  patternsIndexed?: number;
  terminologyIndexed?: number;
  codeChunksIndexed?: number;
}

async function getKnowledgeStatus(projectId: string): Promise<KnowledgeStatus> {
  const response = await fetch(`/api/ai/knowledge/status/${projectId}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
}

async function indexKnowledgeBase(
  projectId: string,
  buildId: number,
  options: { force?: boolean; includeCode?: boolean }
): Promise<IndexResult> {
  const response = await fetch(
    `/api/ai/knowledge/index/${projectId}/${buildId}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        force: options.force ?? false,
        includeCode: options.includeCode ?? false,
      }),
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export function KnowledgeBasePage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [selectedBuildId, setSelectedBuildId] = useState<number | null>(null);
  const [includeCode, setIncludeCode] = useState(true);
  const queryClient = useQueryClient();

  // Fetch branches/builds
  const { data: branchesData } = useQuery({
    queryKey: ['branches', projectId],
    queryFn: () => getBranches(projectId!),
    enabled: !!projectId,
  });

  const deployments = (branchesData?.branches_with_builds || [])
    .filter((b: any) => b.latest_build?.id && (b.branch_type === 'production' || b.branch_type === 'staging'))
    .map((b: any) => ({
      buildId: b.latest_build!.id,
      branchName: b.name,
      branchType: b.branch_type,
    }));

  // Auto-select first deployment
  if (deployments.length > 0 && !selectedBuildId) {
    const production = deployments.find((d: any) => d.branchType === 'production');
    setSelectedBuildId(production?.buildId || deployments[0].buildId);
  }

  // Fetch knowledge status
  const { data: status, isLoading: statusLoading, error: statusError, refetch } = useQuery({
    queryKey: ['knowledgeStatus', projectId],
    queryFn: () => getKnowledgeStatus(projectId!),
    enabled: !!projectId,
    refetchInterval: 10000,
  });

  // Index mutation
  const indexMutation = useMutation({
    mutationFn: () => {
      if (!selectedBuildId) throw new Error('No build selected');
      return indexKnowledgeBase(projectId!, selectedBuildId, {
        force: true,
        includeCode,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledgeStatus', projectId] });
    },
  });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const totalVectors = status?.stats
    ? (status.stats.modelsIndexed || 0) +
      (status.stats.fieldsIndexed || 0) +
      (status.stats.patternsIndexed || 0) +
      (status.stats.terminologyIndexed || 0) +
      (status.stats.codeChunksIndexed || 0)
    : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to={`/project/${projectId}/ai`}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to AI Chat
              </Link>
              <div className="h-6 w-px bg-gray-200" />
              <div className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-purple-600" />
                <h1 className="text-lg font-semibold">Knowledge Base Manager</h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Status Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Vector Database Status
                </CardTitle>
                <CardDescription>
                  Semantic search index for your Odoo schema and code
                </CardDescription>
              </div>
              {status?.indexed ? (
                <Badge className="bg-green-100 text-green-700 border-green-300">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Indexed
                </Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-700 border-amber-300">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Not Indexed
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {statusLoading ? (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading status...
              </div>
            ) : statusError ? (
              <div className="text-red-600">
                Error loading status: {statusError instanceof Error ? statusError.message : 'Unknown error'}
              </div>
            ) : status?.indexed ? (
              <div className="space-y-4">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <StatCard
                    icon={Database}
                    label="Models"
                    value={status.stats?.modelsIndexed || 0}
                    color="blue"
                  />
                  <StatCard
                    icon={FileText}
                    label="Fields"
                    value={status.stats?.fieldsIndexed || 0}
                    color="purple"
                  />
                  <StatCard
                    icon={Zap}
                    label="Patterns"
                    value={status.stats?.patternsIndexed || 0}
                    color="orange"
                  />
                  <StatCard
                    icon={BookOpen}
                    label="Terms"
                    value={status.stats?.terminologyIndexed || 0}
                    color="teal"
                  />
                  <StatCard
                    icon={Code}
                    label="Code Chunks"
                    value={status.stats?.codeChunksIndexed || 0}
                    color="pink"
                  />
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm text-gray-500">
                    <strong>{totalVectors.toLocaleString()}</strong> total vectors indexed
                    {status.indexedAt && (
                      <span className="ml-2">
                        | Last indexed: {formatDate(status.indexedAt)}
                      </span>
                    )}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => refetch()}>
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Refresh
                  </Button>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center">
                <Brain className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">No knowledge base indexed yet</p>
                <p className="text-sm text-gray-500">
                  Index your Odoo schema and custom module code to enable semantic search
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Index Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Index Knowledge Base
            </CardTitle>
            <CardDescription>
              Build or rebuild the vector index for this project
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Deployment Selector */}
            <div className="space-y-2">
              <Label>Select Deployment</Label>
              <Select
                value={selectedBuildId ? String(selectedBuildId) : ''}
                onValueChange={(v) => setSelectedBuildId(parseInt(v, 10))}
              >
                <SelectTrigger className="w-full max-w-md">
                  <SelectValue placeholder="Select a deployment..." />
                </SelectTrigger>
                <SelectContent>
                  {deployments.map((d: any) => (
                    <SelectItem key={d.buildId} value={String(d.buildId)}>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className={
                            d.branchType === 'production'
                              ? 'border-green-500 text-green-600'
                              : 'border-blue-500 text-blue-600'
                          }
                        >
                          {d.branchType}
                        </Badge>
                        {d.branchName}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                The schema and code will be fetched from this deployment
              </p>
            </div>

            {/* Options */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Switch
                  id="include-code"
                  checked={includeCode}
                  onCheckedChange={setIncludeCode}
                  disabled={indexMutation.isPending}
                />
                <div>
                  <Label htmlFor="include-code" className="cursor-pointer">
                    Include custom module code
                  </Label>
                  <p className="text-xs text-gray-500">
                    Index Python code from your custom Odoo modules for better context
                  </p>
                </div>
              </div>
            </div>

            {/* Index Button */}
            <div className="pt-4 border-t">
              <Button
                onClick={() => indexMutation.mutate()}
                disabled={!selectedBuildId || indexMutation.isPending}
                size="lg"
                className="w-full max-w-md"
              >
                {indexMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Indexing... This may take a minute
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {status?.indexed ? 'Re-index Knowledge Base' : 'Index Knowledge Base'}
                  </>
                )}
              </Button>

              {indexMutation.isError && (
                <p className="text-red-600 text-sm mt-2">
                  Error: {indexMutation.error instanceof Error ? indexMutation.error.message : 'Failed to index'}
                </p>
              )}

              {indexMutation.isSuccess && indexMutation.data && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-700 font-medium flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Successfully indexed!
                  </p>
                  <p className="text-sm text-green-600 mt-1">
                    {indexMutation.data.message ||
                      `Indexed ${indexMutation.data.modelsIndexed || 0} models, ${indexMutation.data.fieldsIndexed || 0} fields, ${indexMutation.data.codeChunksIndexed || 0} code chunks`
                    }
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">What gets indexed?</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <Database className="h-4 w-4 mt-0.5 text-blue-500" />
                <span><strong>Models:</strong> All Odoo model names and their descriptions</span>
              </li>
              <li className="flex items-start gap-2">
                <FileText className="h-4 w-4 mt-0.5 text-purple-500" />
                <span><strong>Fields:</strong> Field names, types, labels, and relationships</span>
              </li>
              <li className="flex items-start gap-2">
                <Zap className="h-4 w-4 mt-0.5 text-orange-500" />
                <span><strong>Patterns:</strong> Common SQL query patterns (sales by month, top customers, etc.)</span>
              </li>
              <li className="flex items-start gap-2">
                <BookOpen className="h-4 w-4 mt-0.5 text-teal-500" />
                <span><strong>Terminology:</strong> Odoo-specific terms (partner = customer, etc.)</span>
              </li>
              <li className="flex items-start gap-2">
                <Code className="h-4 w-4 mt-0.5 text-pink-500" />
                <span><strong>Code:</strong> Classes, methods, computed fields from your custom modules</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

interface StatCardProps {
  icon: React.ElementType;
  label: string;
  value: number;
  color: 'blue' | 'purple' | 'orange' | 'teal' | 'pink';
}

function StatCard({ icon: Icon, label, value, color }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700',
    orange: 'bg-orange-100 text-orange-700',
    teal: 'bg-teal-100 text-teal-700',
    pink: 'bg-pink-100 text-pink-700',
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
      <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-800">{value.toLocaleString()}</p>
        <p className="text-xs text-gray-500">{label}</p>
      </div>
    </div>
  );
}
